import type * as React from 'react'

import { cn } from '@/lib/utils'

/**
 * `stack` turns the table into stacked rows below 640 px; label the cells with
 * `data-label`, `data-grow` or `data-full` (see index.css).
 */
function Table({
  className,
  stack = false,
  ...props
}: React.ComponentProps<'table'> & { stack?: boolean }) {
  return (
    <div className="relative w-full overflow-x-auto">
      <table
        className={cn('w-full caption-bottom text-sm', stack && 'stack-table', className)}
        {...props}
      />
    </div>
  )
}

function TableHeader({ className, ...props }: React.ComponentProps<'thead'>) {
  return <thead className={cn('[&_tr]:border-border [&_tr]:border-b', className)} {...props} />
}

function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
  return <tbody className={cn('[&_tr:last-child]:border-0', className)} {...props} />
}

function TableRow({ className, ...props }: React.ComponentProps<'tr'>) {
  return (
    <tr
      className={cn(
        'hover:bg-foreground/4 data-[state=selected]:bg-steel-100 border-foreground/8 border-b',
        className,
      )}
      {...props}
    />
  )
}

function TableHead({ className, ...props }: React.ComponentProps<'th'>) {
  return (
    <th
      className={cn(
        'text-foreground/60 h-9 px-2.5 text-left align-middle text-[11px] font-medium tracking-[0.08em] whitespace-nowrap uppercase',
        className,
      )}
      {...props}
    />
  )
}

function TableCell({ className, ...props }: React.ComponentProps<'td'>) {
  return <td className={cn('px-2.5 py-2 align-middle', className)} {...props} />
}

function TableCaption({ className, ...props }: React.ComponentProps<'caption'>) {
  return <caption className={cn('text-muted-foreground mt-4 text-sm', className)} {...props} />
}

export { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow }
